/**
 * Support → one request (D-129): the exchanges (public answers and internal notes), the answer
 * box with saved replies, files and the AI draft (never sent without a human), the deadline,
 * status, priority, assignment, tags, duplicates, refund request and the full history.
 */

import "./support.css";

import {
  type SupportEvent,
  type SupportMacro,
  type SupportTicketDetail,
  type SupportTicketPriorityEnum,
  type SupportTicketStatusEnum,
  useAuthMeRetrieve,
  useSupportMacrosList,
  useSupportRefundsCreate,
  useSupportTicketsAssignCreate,
  useSupportTicketsList,
  useSupportTicketsMergeCreate,
  useSupportTicketsPriorityCreate,
  useSupportTicketsReplyCreate,
  useSupportTicketsRetrieve,
  useSupportTicketsStatusCreate,
  useSupportTicketsSuggestCreate,
  useSupportTicketsTagsCreate,
  useSupportLearnersRetrieve,
} from "@agencyos/api-client";
import { checkFile, formatXaf, priorityTone, statusTone } from "@agencyos/core";
import { Badge, type BadgeTone, Button, Checkbox, GlassCard, SelectField, Skeleton, TextAreaField, TextField } from "@agencyos/ui-web";
import { type ChangeEvent, type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { Page } from "../../components/AppShell";
import { fieldErrors } from "../../lib/settings";
import { dateText, dateTimeText } from "../billing/shared";
import { PRIORITIES, STATUSES, TimerBadge, useRefreshSupport, useSupportConfig, useSupportPermissions } from "./shared";

interface AiDraft {
  status?: string;
  text?: string;
  missing?: string;
}

function fill(text: string, ticket: SupportTicketDetail, agency: string): string {
  return text.replaceAll("{name}", ticket.requester_name || "").replaceAll("{number}", ticket.number).replaceAll("{agency}", agency);
}

function Thread({ ticket }: { ticket: SupportTicketDetail }) {
  const { t, i18n } = useTranslation();
  return (
    <GlassCard as="section" className="stack" aria-labelledby="support-thread-title">
      <h2 id="support-thread-title">{t("support.ticket.thread")}</h2>
      <ol className="plain-list support-thread">
        {ticket.messages.map((message) => (
          <li
            key={message.id}
            className={`bubble ${message.from_customer ? "bubble--in" : "bubble--out"}${message.kind === "internal" ? " support-note" : ""}`}
          >
            <p className="bubble__meta">
              {message.kind === "internal" ? <Badge tone="warning">{t("support.ticket.internal")}</Badge> : null}{" "}
              {message.from_customer ? t("support.ticket.fromCustomer") : message.author_name} · {dateTimeText(message.created_at, i18n.language)}
              {message.from_ai_draft ? <small className="muted"> · {t("support.ticket.aiDraft")}</small> : null}
            </p>
            <p className="bubble__body support-body">{message.body}</p>
            {message.attachments.length ? (
              <ul className="plain-list">
                {message.attachments.map((file) => (
                  <li key={file.id}>
                    {file.url ? (
                      <a href={file.url} target="_blank" rel="noreferrer">
                        {file.name}
                      </a>
                    ) : (
                      file.name
                    )}
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ol>
    </GlassCard>
  );
}

function Composer({ ticket, agency }: { ticket: SupportTicketDetail; agency: string }) {
  const { t } = useTranslation();
  const refresh = useRefreshSupport();
  const config = useSupportConfig();
  const macros = useSupportMacrosList({ category: ticket.category });
  const reply = useSupportTicketsReplyCreate({ mutation: { onSuccess: refresh } });
  const suggest = useSupportTicketsSuggestCreate({ mutation: { onSuccess: refresh } });
  const [body, setBody] = useState("");
  const [internal, setInternal] = useState(false);
  const [status, setStatus] = useState<"" | SupportTicketStatusEnum>("");
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState("");
  const [fromDraft, setFromDraft] = useState(false);
  const draft = (ticket.ai_draft ?? {}) as AiDraft;
  const errors = fieldErrors(reply.error);
  const rows: SupportMacro[] = macros.data?.status === 200 ? macros.data.data.results.filter((m) => m.is_active !== false) : [];
  const pending = draft.status === "pending";
  // the AI draft is written by the worker: read the ticket again while it is pending
  useEffect(() => {
    if (!pending) return;
    const timer = window.setInterval(refresh, 2500);
    return () => window.clearInterval(timer);
  }, [pending, refresh]);

  const onFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = Array.from(event.target.files ?? []);
    for (const file of chosen) {
      const verdict = checkFile(file, config?.attachments ?? {});
      if (verdict !== "ok") {
        setFileError(t(verdict === "type" ? "support.ticket.fileType" : "support.ticket.fileSize", { name: file.name }));
        setFiles([]);
        return;
      }
    }
    setFileError("");
    setFiles(chosen);
  };
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    reply.mutate(
      { id: ticket.id, data: { body: body.trim(), internal, from_ai_draft: fromDraft, files, ...(status ? { status } : {}) } },
      {
        onSuccess: (response) => {
          if (response.status !== 200) return;
          setBody("");
          setFiles([]);
          setStatus("");
          setFromDraft(false);
        },
      },
    );
  };
  if (ticket.status === "closed") return null;
  return (
    <GlassCard as="section" className="stack" aria-labelledby="support-reply-title">
      <h2 id="support-reply-title">{internal ? t("support.ticket.note") : t("support.ticket.reply")}</h2>
      <div className="row support-tools">
        <SelectField
          label={t("support.ticket.macro")}
          value=""
          onChange={(e) => {
            const macro = rows.find((m) => m.id === e.target.value);
            if (!macro) return;
            const texts = (macro.body ?? {}) as Record<string, string>;
            const text = texts[ticket.language] ?? texts.fr ?? texts.en ?? "";
            setBody((current) => `${current}${current ? "\n\n" : ""}${fill(text, ticket, agency)}`);
          }}
          options={[{ value: "", label: t("support.ticket.noMacro") }, ...rows.map((m) => ({ value: m.id, label: m.title }))]}
        />
        <Button type="button" variant="secondary" disabled={suggest.isPending || pending} onClick={() => suggest.mutate({ id: ticket.id })}>
          {pending ? t("support.ticket.suggesting") : t("support.ticket.suggest")}
        </Button>
      </div>
      {draft.status === "ready" && draft.text ? (
        <div className="support-draft stack" role="status">
          <p className="muted">{t("support.ticket.draftWarning")}</p>
          <p className="support-body">{draft.text}</p>
          {draft.missing ? <p className="muted">{t("support.ticket.draftMissing", { text: draft.missing })}</p> : null}
          <div className="row">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setBody(draft.text ?? "");
                setFromDraft(true);
                setInternal(false);
              }}
            >
              {t("support.ticket.useDraft")}
            </Button>
          </div>
        </div>
      ) : null}
      {draft.status === "blocked" || draft.status === "empty" ? <p role="status" className="muted">{t("support.ticket.draftBlocked")}</p> : null}
      {draft.status === "unavailable" ? <p role="status" className="muted">{t("support.ticket.draftUnavailable")}</p> : null}
      <form className="stack" onSubmit={onSubmit} noValidate aria-label={t("support.ticket.reply")}>
        <TextAreaField label={t("support.ticket.body")} value={body} rows={6} onChange={(e) => setBody(e.target.value)} error={errors.body ?? errors.detail} />
        <Checkbox label={t("support.ticket.internalToggle")} checked={internal} onChange={(e) => setInternal(e.target.checked)} />
        <div className="form-grid">
          <SelectField
            label={t("support.ticket.statusAfter")}
            value={status}
            onChange={(e) => setStatus(e.target.value as "" | SupportTicketStatusEnum)}
            options={[{ value: "", label: t("support.ticket.keepStatus") }, ...STATUSES.filter((s) => s !== "new").map((s) => ({ value: s, label: t(`support.status.${s}`) }))]}
          />
          <label className="ao-field">
            <span className="ao-field__label">{t("support.ticket.files")}</span>
            <input type="file" multiple accept={(config?.attachments?.types ?? []).join(",")} onChange={onFiles} />
            <small className="ao-field__hint">{t("support.ticket.filesHint")}</small>
            {fileError || errors.files ? (
              <small role="alert" className="ao-field__error">
                {fileError || errors.files}
              </small>
            ) : null}
          </label>
        </div>
        <div className="row">
          <Button type="submit" disabled={reply.isPending || !body.trim() || Boolean(fileError)}>
            {reply.isPending ? t("common.loading") : t("support.ticket.send")}
          </Button>
          {reply.isSuccess && !body ? <span role="status">{t("support.ticket.sent")}</span> : null}
        </div>
      </form>
    </GlassCard>
  );
}

function eventText(event: SupportEvent, t: (key: string, values?: Record<string, unknown>) => string, exists: (key: string) => boolean): string {
  const data = (event.data ?? {}) as Record<string, unknown>;
  const values: Record<string, unknown> = { ...data, action: event.action };
  if (event.action === "status") {
    values.before = t(`support.status.${String(data.before)}`);
    values.after = t(`support.status.${String(data.after)}`);
  }
  if (event.action === "priority") {
    values.before = t(`support.priority.${String(data.before)}`);
    values.after = t(`support.priority.${String(data.after)}`);
  }
  const key = `support.ticket.events.${event.action}`;
  return exists(key) ? t(key, values) : t("support.ticket.events.other", values);
}

interface RefundTarget {
  value: string;
  label: string;
  kind: "subscription" | "invoice";
  max: number;
}

/** A refund request on the ticket's invoice or on a paid Academy subscription of the learner. */
function RefundBox({ ticket }: { ticket: SupportTicketDetail }) {
  const { t, i18n } = useTranslation();
  const refresh = useRefreshSupport();
  const refund = useSupportRefundsCreate({ mutation: { onSuccess: refresh } });
  const card = useSupportLearnersRetrieve(ticket.learner ?? "", { query: { enabled: Boolean(ticket.learner) } });
  const learner = card.data?.status === 200 ? (card.data.data.learner as { subscriptions?: { id: string; refundable: boolean; amount_xaf: number; ends_on: string | null; invoice: string }[] } | null) : null;
  const targets: RefundTarget[] = [
    ...(ticket.invoice ? [{ value: `invoice:${ticket.invoice}`, label: t("support.ticket.invoice", { number: ticket.invoice_number }), kind: "invoice" as const, max: 0 }] : []),
    ...(learner?.subscriptions ?? [])
      .filter((s) => s.refundable)
      .map((s) => ({
        value: `subscription:${s.id}`,
        label: `${t("support.ticket.refundSubscription")} · ${formatXaf(s.amount_xaf)}${s.invoice ? ` · ${s.invoice}` : ""}${s.ends_on ? ` · ${dateText(s.ends_on, i18n.language)}` : ""}`,
        kind: "subscription" as const,
        max: s.amount_xaf,
      })),
  ];
  const [form, setForm] = useState({ target: "", amount: "", reason: "" });
  const errors = fieldErrors(refund.error);
  const chosen = targets.find((row) => row.value === (form.target || targets[0]?.value));
  return (
    <GlassCard as="section" className="stack" aria-labelledby="support-refund-title">
      <h2 id="support-refund-title">{t("support.ticket.refund")}</h2>
      <p className="muted">{t("support.ticket.refundIntro")}</p>
      {ticket.refunds.map((row) => (
        <p key={row.id}>
          {formatXaf(row.amount_xaf)} ·{" "}
          <Badge tone={row.status === "approved" ? "success" : row.status === "rejected" ? "danger" : "warning"}>{t(`support.refunds.statuses.${row.status}`)}</Badge>
        </p>
      ))}
      {targets.length ? (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            if (!chosen) return;
            const id = chosen.value.split(":")[1] ?? "";
            refund.mutate({
              data: {
                ticket: ticket.id,
                ...(chosen.kind === "invoice" ? { invoice: id } : { subscription: id }),
                amount_xaf: Number(form.amount),
                reason: form.reason.trim(),
              },
            });
          }}
        >
          <SelectField label={t("support.ticket.refund")} value={chosen?.value ?? ""} onChange={(e) => setForm({ ...form, target: e.target.value })} options={targets.map(({ value, label }) => ({ value, label }))} />
          <TextField type="number" min={1} max={chosen?.max || undefined} label={t("support.ticket.refundAmount")} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} error={errors.amount_xaf} />
          <TextField label={t("support.ticket.refundReason")} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} error={errors.reason ?? errors.detail} />
          <Button type="submit" variant="secondary" disabled={refund.isPending || !form.amount || !form.reason.trim()}>
            {t("support.ticket.refundAsk")}
          </Button>
          {refund.isSuccess ? <span role="status">{t("support.ticket.refundAsked")}</span> : null}
        </form>
      ) : null}
    </GlassCard>
  );
}

function Side({ ticket }: { ticket: SupportTicketDetail }) {
  const { t, i18n } = useTranslation();
  const can = useSupportPermissions();
  const config = useSupportConfig();
  const me = useAuthMeRetrieve();
  const myId = me.data?.status === 200 ? me.data.data.id : "";
  const refresh = useRefreshSupport();
  const options = { mutation: { onSuccess: refresh } };
  const setStatus = useSupportTicketsStatusCreate(options);
  const setPriority = useSupportTicketsPriorityCreate(options);
  const assign = useSupportTicketsAssignCreate(options);
  const tags = useSupportTicketsTagsCreate(options);
  const merge = useSupportTicketsMergeCreate(options);
  const [tagText, setTagText] = useState(Array.isArray(ticket.tags) ? (ticket.tags as string[]).join(", ") : "");
  const [mergeNumber, setMergeNumber] = useState("");
  const candidates = useSupportTicketsList({ q: mergeNumber.trim() }, { query: { enabled: mergeNumber.trim().length >= 5 } });
  const target = candidates.data?.status === 200 ? candidates.data.data.results.find((row) => row.number === mergeNumber.trim() && row.id !== ticket.id) : undefined;
  const work = can("support.tickets.work");
  const language = i18n.language === "en" ? "en" : "fr";
  const teams = Object.entries(config?.teams ?? {});
  const members = config?.members ?? [];
  const cardLink = ticket.learner ? `/support/learners/${ticket.learner}` : ticket.contact ? `/support/customers/${ticket.contact}` : "";
  return (
    <aside className="detail-layout__aside stack">
      <GlassCard as="section" className="stack" aria-labelledby="support-side-title">
        <h2 id="support-side-title">{t("support.ticket.details")}</h2>
        <TimerBadge timer={ticket.timer} />
        <p>
          <strong>{t("support.ticket.requester")}</strong>
          <br />
          {ticket.requester_name || "—"}
          <br />
          <small className="muted">
            {ticket.requester_email} {ticket.requester_phone}
          </small>
        </p>
        {cardLink && can("support.customers.read") ? <Link to={cardLink}>{t("support.ticket.openCard")}</Link> : null}
        {!cardLink ? <p className="muted">{t("support.ticket.noCard")}</p> : null}
        {ticket.invoice_number || ticket.dossier_reference || ticket.conversation ? (
          <p className="muted">
            {t("support.ticket.linked")} :{" "}
            {[
              ticket.invoice_number ? t("support.ticket.invoice", { number: ticket.invoice_number }) : "",
              ticket.dossier_reference ? t("support.ticket.dossier", { reference: ticket.dossier_reference }) : "",
              ticket.conversation ? t("support.ticket.conversation") : "",
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        ) : null}
        {ticket.merged_into_number ? <Badge tone="info">{t("support.ticket.mergedInto", { number: ticket.merged_into_number })}</Badge> : null}
        {ticket.csat_score ? (
          <p>
            <Badge tone={ticket.csat_score >= 4 ? "success" : "warning"}>{t("support.ticket.csat", { score: ticket.csat_score })}</Badge>
            {ticket.csat_resolved === false ? <small className="muted"> {t("support.ticket.csatNotSolved")}</small> : null}
            {ticket.csat_comment ? <small className="muted"> « {ticket.csat_comment} »</small> : null}
          </p>
        ) : null}
        <SelectField
          label={t("support.ticket.statusLabel")}
          value={ticket.status}
          disabled={!work || ticket.status === "closed"}
          onChange={(e) => setStatus.mutate({ id: ticket.id, data: { status: e.target.value as SupportTicketStatusEnum } })}
          options={STATUSES.map((s) => ({ value: s, label: t(`support.status.${s}`) }))}
        />
        <SelectField
          label={t("support.ticket.priorityLabel")}
          value={ticket.priority}
          disabled={!work || ticket.status === "closed"}
          onChange={(e) => setPriority.mutate({ id: ticket.id, data: { priority: e.target.value as SupportTicketPriorityEnum } })}
          options={PRIORITIES.map((p) => ({ value: p, label: t(`support.priority.${p}`) }))}
        />
        <SelectField
          label={t("support.ticket.assignee")}
          value={ticket.assignee ?? ""}
          disabled={!work}
          onChange={(e) => assign.mutate({ id: ticket.id, data: { assignee: e.target.value || null } })}
          options={[{ value: "", label: t("support.list.nobody") }, ...members.map((m) => ({ value: m.id, label: m.name }))]}
        />
        {work && myId && ticket.assignee !== myId ? (
          <Button type="button" variant="secondary" onClick={() => assign.mutate({ id: ticket.id, data: { assignee: myId } })}>
            {t("support.ticket.takeIt")}
          </Button>
        ) : null}
        <SelectField
          label={t("support.ticket.team")}
          value={ticket.team}
          disabled={!work}
          onChange={(e) => assign.mutate({ id: ticket.id, data: { assignee: ticket.assignee, team: e.target.value } })}
          options={teams.map(([key, label]) => ({ value: key, label: label[language] ?? key }))}
        />
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            tags.mutate({ id: ticket.id, data: { tags: tagText.split(",").map((s) => s.trim()).filter(Boolean) } });
          }}
        >
          <TextField label={t("support.ticket.tags")} hint={t("support.ticket.tagsHint")} value={tagText} disabled={!work} onChange={(e) => setTagText(e.target.value)} />
          {work ? (
            <Button type="submit" variant="ghost">
              {t("support.ticket.save")}
            </Button>
          ) : null}
        </form>
      </GlassCard>
      {work && ticket.status !== "closed" ? (
        <GlassCard as="section" className="stack" aria-labelledby="support-merge-title">
          <h2 id="support-merge-title">{t("support.ticket.merge")}</h2>
          <TextField label={t("support.ticket.mergeTarget")} hint={t("support.ticket.mergeHint")} value={mergeNumber} onChange={(e) => setMergeNumber(e.target.value)} />
          {mergeNumber.trim().length >= 5 && !target && !candidates.isPending ? <p className="muted">{t("support.ticket.mergeNotFound")}</p> : null}
          <Button type="button" variant="secondary" disabled={!target || merge.isPending} onClick={() => target && merge.mutate({ id: ticket.id, data: { target: target.id } })}>
            {t("support.ticket.mergeDo")}
          </Button>
        </GlassCard>
      ) : null}
      {can("support.refunds.request") && (ticket.invoice || ticket.learner) ? <RefundBox ticket={ticket} /> : null}
      <GlassCard as="section" className="stack" aria-labelledby="support-history-title">
        <h2 id="support-history-title">{t("support.ticket.history")}</h2>
        <ol className="timeline">
          {ticket.events.map((event) => (
            <li key={event.id} className="timeline__row">
              <span className="timeline__time">{dateTimeText(event.created_at, i18n.language)}</span>
              <span className="timeline__body">
                {eventText(event, t, (k) => i18n.exists(k))}
                {event.actor_name ? <small className="muted"> · {event.actor_name}</small> : null}
              </span>
            </li>
          ))}
        </ol>
      </GlassCard>
    </aside>
  );
}

export function TicketPage() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const query = useSupportTicketsRetrieve(id);
  const ticket = query.data?.status === 200 ? query.data.data : undefined;
  const me = useAuthMeRetrieve();
  const agency = me.data?.status === 200 ? (me.data.data.memberships[0]?.organization_name ?? "") : "";
  if (!ticket) {
    return <Page title={t("support.title")}>{query.isPending ? <Skeleton style={{ height: 300 }} /> : <p role="alert">{t("common.error")}</p>}</Page>;
  }
  return (
    <Page
      eyebrow={<Link to="/support">{t("support.ticket.back")}</Link>}
      title={`${ticket.number} · ${ticket.subject}`}
      actions={
        <div className="row support-chips">
          <Badge>{t(`support.category.${ticket.category}`)}</Badge>
          {ticket.is_complaint && ticket.category !== "complaint" ? <Badge tone="danger">{t("support.list.complaint")}</Badge> : null}
          <Badge tone={priorityTone(ticket.priority) as BadgeTone}>{t(`support.priority.${ticket.priority}`)}</Badge>
          <Badge tone={statusTone(ticket.status) as BadgeTone}>{t(`support.status.${ticket.status}`)}</Badge>
        </div>
      }
    >
      <div className="detail-layout">
        <div className="detail-layout__main stack">
          <Thread ticket={ticket} />
          <Composer ticket={ticket} agency={agency} />
        </div>
        <Side ticket={ticket} />
      </div>
    </Page>
  );
}
